import { Box, Container, Grid, Paper, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { m } from 'framer-motion';
import AnimatedCounter from 'src/components/custom/AnimationCounter';
import { useResponsive } from 'src/hooks/use-responsive';
import { useLocales } from 'src/locales';
import { bgGradient } from 'src/theme/css';
import { secondaryFont } from 'src/theme/typography';

const StyledTextGradient = styled(m.h1)(({ theme }) => ({
  background: '-webkit-linear-gradient(0deg, #164732, #51B949)',
  '-webkit-background-clip': 'text',
  '-webkit-text-fill-color': 'transparent',
  textTransform: 'uppercase',
  fontSize: `20px`,
  fontFamily: secondaryFont,
}));

function FoudationView() {
  const { t } = useLocales();
  const theme = useTheme();

  const mdDown = useResponsive('down', 'md');

  const config = [
    {
      title: t('string_031'),
      descriptions: [t('string_032'), t('string_033')],
      color: '#EDFFEB',
    },
    {
      title: t('string_034'),
      descriptions: [t('string_035')],
      color: '#DAFFD7',
    },
    {
      title: t('string_036'),
      descriptions: [t('string_037')],
      color: '#C8FFC4',
    },
    {
      title: t('string_038'),
      descriptions: [t('string_039')],
      color: '#B6FFB0',
    },
    {
      title: t('string_040'),
      descriptions: [t('string_041')],
      color: '#A4FF9C',
    },
  ];

  return (
    <Container>
      <Stack py={12}>
        <Typography fontSize={40} textAlign="center">
          <span style={{ color: theme.palette.primary.main }}>{t('string_025.1')}</span>{' '}
          {t('string_025.2')}{' '}
          <span style={{ color: theme.palette.primary.main }}>{t('string_025.3')}</span>
        </Typography>

        <Grid container justifyContent="center" pt={12}>
          <Grid item xs={12} sm={8} md={4}>
            <Stack>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <AnimatedCounter
                  target={100}
                  sx={{ fontSize: 70, fontWeight: '800', color: theme.palette.primary.main }}
                />
                <Typography
                  sx={{ fontSize: 70, fontWeight: '800', color: theme.palette.primary.main }}
                >
                  +
                </Typography>
              </Stack>
              <Typography fontSize={20} textAlign="center">
                {t('string_018')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <Stack>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <AnimatedCounter
                  target={12}
                  sx={{ fontSize: 70, fontWeight: '800', color: theme.palette.primary.main }}
                />
                <Typography
                  sx={{ fontSize: 70, fontWeight: '800', color: theme.palette.primary.main }}
                >
                  M+
                </Typography>
              </Stack>
              <Typography fontSize={20} textAlign="center">
                {t('string_026')}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Grid container pt={12} spacing={6} justifyContent="center">
          <Grid item xs={12} sm={8} md={6}>
            <Stack
              component={Paper}
              p={4}
              sx={{
                ...bgGradient({
                  direction: '90deg',
                  startColor: '#BBFFB5',
                  endColor: '#E2FFDF',
                }),
              }}
            >
              <StyledTextGradient
                animate={{ backgroundPosition: '50% center' }}
                sx={{ textTransform: 'uppercase' }}
              >
                {t('string_027')}
              </StyledTextGradient>
              <Typography fontSize={20}>{t('string_028')}</Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} md={6}>
            <Stack
              component={Paper}
              p={4}
              sx={{
                ...bgGradient({
                  direction: '-90deg',
                  startColor: '#BBFFB5',
                  endColor: '#E2FFDF',
                }),
              }}
            >
              <StyledTextGradient
                animate={{ backgroundPosition: '50% center' }}
                sx={{ textTransform: 'uppercase' }}
              >
                {t('string_029')}
              </StyledTextGradient>
              <Typography fontSize={20}>{t('string_030')}</Typography>
            </Stack>
          </Grid>
        </Grid>

        <Grid container pt={6} alignItems="center" justifyContent="center" spacing={6}>
          <Grid item xs={12} sm={8} md={6}>
            <Stack height="100%">
              <Typography fontSize={mdDown ? 40 : 50} textAlign={mdDown ? 'center' : 'left'}>
                {t('string_042')}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} md={6}>
            <Stack>
              {config.map((x, i) => (
                <Stack
                  key={i}
                  component={Paper}
                  p={4}
                  sx={{
                    mb: -8,
                    background: x.color,
                    boxShadow: `rgba(0, 0, 0, 0.35) 0px -5px 15px`,
                    transition: theme.transitions.create('all'),
                    '&:hover': {
                      mb: 2,
                    },
                  }}
                >
                  <StyledTextGradient
                    animate={{ backgroundPosition: '50% center' }}
                    sx={{ textTransform: 'uppercase' }}
                  >
                    {x.title}
                  </StyledTextGradient>
                  <Stack spacing={1}>
                    {x.descriptions.map((y, j) => (
                      <Typography key={j} fontSize={20}>
                        {y}
                      </Typography>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Stack pt={12}>
          <Typography fontSize={mdDown ? 40 : 50} textAlign="center" mb={6}>
            {t('string_043')}
          </Typography>

          <Grid container spacing={3}>
            {Array(6)
              .fill(' ')
              .map((x, i) => (
                <Grid key={i} item xs={6} md={4}>
                  <Box
                    component="img"
                    srcSet={`/assets/images/foudation/foudation-00${i + 1}.png 2x`}
                    alt=""
                    sx={{ width: '100%' }}
                  />
                </Grid>
              ))}
          </Grid>

          <StyledTextGradient
            sx={{
              fontSize: mdDown ? 22 : 32,
              textTransform: 'uppercase',
              textAlign: 'center',
              mt: 6,
            }}
          >
            {t('string_044')}
          </StyledTextGradient>
        </Stack>
      </Stack>
    </Container>
  );
}

export default FoudationView;
